import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppSettings } from '@/context/AppSettingsContext';
import { ChevronDown, Flag, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '../ui/collapsible';
import { SOP, SOPFilters, SOPStats } from '../../types/sop';
import { SOPWithSummary } from '../../types/sopSummary';
import { sopService } from '../../services/firebase/SOPService';
import { AddToFavoritesDialog } from '../dialogs/AddToFavoritesDialog';
import { FlagForReviewDialog } from '../dialogs/FlagForReviewDialog';
import { RemoveFlagDialog } from '../dialogs/RemoveFlagDialog';
import { getDepartmentColorClass } from '@/utils/departmentColors';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const ITEMS_PER_PAGE = 50; // Increased to show more items at once

type SortOrder = 'newest' | 'oldest';

const SOPList: React.FC = () => {
  const { isAdmin } = useIsAdmin();
  const { appSettings } = useAppSettings();
  const [sops, setSOPs] = useState<SOPWithSummary[]>([]);
  const [filteredSops, setFilteredSops] = useState<SOPWithSummary[]>([]);
  const [displayedSops, setDisplayedSops] = useState<SOPWithSummary[]>([]);
  const [stats, setStats] = useState<SOPStats | null>(null);
  const [syncStatus, setSyncStatus] = useState<{ lastSync: Date | null }>({ lastSync: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const loadMoreRef = useRef<HTMLDivElement>(null);
  
  const [filters, setFilters] = useState<SOPFilters>({
    department: '',
    author: '',
    tag: '',
    searchQuery: '',
    flaggedOnly: false
  });

  // Load SOPs and calculate stats
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Fetch both SOPs and sync status in parallel
        const [allSOPs, status] = await Promise.all([
          sopService.getSOPsWithSummaries(),
          sopService.getSyncStatus()
        ]);
        setSOPs(allSOPs);
        setSyncStatus(status);
        
        // Calculate stats
        const departments = new Set<string>();
        const authors = new Set<string>();
        const tagCounts: Record<string, number> = {};

        allSOPs.forEach(sop => {
          if (sop.department) {
            sop.department.split(',').forEach(dept => departments.add(dept.trim()));
          }
          if (sop.author1) authors.add(sop.author1);
          if (sop.author2) authors.add(sop.author2);
          sop.tags?.forEach(tag => {
            tagCounts[tag] = (tagCounts[tag] || 0) + 1;
          });
        });

        const departmentCounts = Array.from(departments).reduce((acc: Record<string, number>, dept) => {
          acc[dept] = allSOPs.filter(sop => sop.department.includes(dept)).length;
          return acc;
        }, {});

        const authorCounts = Array.from(authors).reduce((acc: Record<string, number>, author) => {
          acc[author] = allSOPs.filter(sop => sop.author1 === author || sop.author2 === author).length;
          return acc;
        }, {});

        setStats({
          totalSOPs: allSOPs.length,
          departmentCounts,
          authorCounts,
          tagCounts,
          latestSOPDate: allSOPs[0]?.dateCreated || new Date().toISOString().split('T')[0]
        });

        // Sort SOPs by date initially with improved date parsing
        const parseDate = (dateStr: string) => {
          if (!dateStr || dateStr.includes('http')) return new Date(0); // Invalid date
          
          // Try MM/DD/YY format first
          const mmddyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
          if (mmddyyMatch) {
            const [_, month, day, year] = mmddyyMatch;
            return new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
          }
          
          // Try MM/DD/YYYY format
          const mmddyyyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
          if (mmddyyyyMatch) {
            const [_, month, day, year] = mmddyyyyMatch;
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          }
          
          // Try direct parsing as a fallback
          const directDate = new Date(dateStr);
          if (!isNaN(directDate.getTime())) {
            return directDate;
          }
          
          return new Date(0); // Invalid date as fallback
        };

        const sortedSOPs = [...allSOPs].sort((a, b) => {
          const dateA = parseDate(a.dateCreated);
          const dateB = parseDate(b.dateCreated);

          // Handle invalid dates by putting them at the end
          if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
          if (isNaN(dateA.getTime())) return 1;
          if (isNaN(dateB.getTime())) return -1;

          return dateB.getTime() - dateA.getTime(); // Newest first by default
        });

        // Set initial filtered SOPs
        setFilteredSops(sortedSOPs);
        setDisplayedSops(sortedSOPs.slice(0, ITEMS_PER_PAGE));
        setError(null);
      } catch (err) {
        setError('Failed to load SOPs. Please try again later.');
        console.error('Error loading SOPs:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []); // No dependencies since we use cache

  // Function to refresh SOPs after flagging/unflagging
  const refreshSOPs = async () => {
    try {
      setLoading(true);
      const allSOPs = await sopService.getSOPsWithSummaries();
      setSOPs(allSOPs);
      
      // Re-apply filters and sorting with the updated data
      // This will trigger the useEffect that handles filtering
      setFilteredSops(allSOPs);
    } catch (error) {
      console.error('Error refreshing SOPs:', error);
      setError('Failed to refresh SOPs. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters and sort order
  useEffect(() => {
    if (sops.length === 0) return;

    // Apply filters in memory
    let result = sops.filter(sop => {
      const matchesSearch = !filters.searchQuery || 
        sop.title.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
        sop.department.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
        (sop.author1 && sop.author1.toLowerCase().includes(filters.searchQuery.toLowerCase())) ||
        (sop.author2 && sop.author2.toLowerCase().includes(filters.searchQuery.toLowerCase())) ||
        sop.tags?.some(tag => tag.toLowerCase().includes(filters.searchQuery.toLowerCase())) ||
        // Search in both summaryies
        (sop.shortSummary && sop.shortSummary.toLowerCase().includes(filters.searchQuery.toLowerCase())) ||
        (sop.detailedSummary && sop.detailedSummary.toLowerCase().includes(filters.searchQuery.toLowerCase())) ||
        // Search in hidden keywords
        (sop.keywords && sop.keywords.some(keyword => keyword.toLowerCase().includes(filters.searchQuery.toLowerCase())));

      const matchesDepartment = !filters.department || filters.department === 'all' ||
        sop.department.split(',').some(dept => dept.trim() === filters.department);

      const matchesAuthor = !filters.author || filters.author === 'all' ||
        sop.author1 === filters.author || sop.author2 === filters.author;

      const matchesTag = !filters.tag || filters.tag === 'all' ||
        sop.tags?.includes(filters.tag);
      
      const matchesFlagFilter = !filters.flaggedOnly || sop.flaggedForReview === true;

      return matchesSearch && matchesDepartment && matchesAuthor && matchesTag && matchesFlagFilter;
    });

    // Sort by date with improved date parsing
    result = [...result].sort((a, b) => {
      // Parse dates with improved handling of different formats
      const parseDate = (dateStr: string) => {
        if (!dateStr || dateStr.includes('http')) return new Date(0); // Invalid date
        
        // Try MM/DD/YY format first
        const mmddyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
        if (mmddyyMatch) {
          const [_, month, day, year] = mmddyyMatch;
          return new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        
        // Try MM/DD/YYYY format
        const mmddyyyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (mmddyyyyMatch) {
          const [_, month, day, year] = mmddyyyyMatch;
          return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        
        // Try direct parsing as a fallback
        const directDate = new Date(dateStr);
        if (!isNaN(directDate.getTime())) {
          return directDate;
        }
        
        return new Date(0); // Invalid date as fallback
      };

      const dateA = parseDate(a.dateCreated);
      const dateB = parseDate(b.dateCreated);

      // Handle invalid dates by putting them at the end
      if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
      if (isNaN(dateA.getTime())) return 1;
      if (isNaN(dateB.getTime())) return -1;
      
      return sortOrder === 'newest' ? dateB.getTime() - dateA.getTime() : dateA.getTime() - dateB.getTime();
    });

    setFilteredSops(result);
    setPage(1);
    setDisplayedSops(result.slice(0, ITEMS_PER_PAGE));
  }, [filters, sortOrder, sops]);

  // Update displayed SOPs when page changes
  useEffect(() => {
    setDisplayedSops(filteredSops.slice(0, page * ITEMS_PER_PAGE));
  }, [page, filteredSops]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !loading && displayedSops.length < filteredSops.length) {
          setPage(prev => prev + 1);
        }
      },
      { threshold: 1.0 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [loading, displayedSops.length, filteredSops.length]);

  // Handle filter changes
  const handleFilterChange = (key: keyof SOPFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle search input
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFilterChange('searchQuery', event.target.value);
  };

  // Handle sort change
  const handleSortChange = (value: string) => {
    setSortOrder(value as SortOrder);
  };

  // Handle clearing all filters
  const clearFilters = () => {
    setFilters({
      department: '', author: '', tag: '', searchQuery: '', flaggedOnly: false
    });
    window.scrollTo(0, 0);
  };

  return (
    <div className="container mx-auto px-10 lg:px-20 py-10 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="flex flex-col space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">SOP List</h1>
          <div className="text-right">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {stats && `${stats.totalSOPs} SOPs available`}
            </div>
            {syncStatus?.lastSync && (
              <p className="text-xs mt-1 flex items-center justify-end text-yellow-500 dark:text-yellow-400">
                <Clock className="h-3 w-3 mr-1" />
                Last Sync: {syncStatus.lastSync.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </p>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 gap-4">
          {/* Search Bar and Flagged Filter */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search titles and summaries..."
                value={filters.searchQuery}
                onChange={handleSearch}
                className="w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
            {appSettings.showSOPFlaggingFeature && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="flagged-filter"
                  checked={filters.flaggedOnly}
                  onChange={(e) => setFilters(prev => ({ ...prev, flaggedOnly: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 mr-2"
                />
                <label htmlFor="flagged-filter" className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  Show only flagged SOPs
                </label>
              </div>
            )}
          </div>

          {/* Filter Controls */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-4 items-center">
              <Select
                value={filters.department}
                onValueChange={(value) => handleFilterChange('department', value)}
              >
                <SelectTrigger className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600">
                  <SelectItem value="all" className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">All Departments</SelectItem>
                  {Object.keys(stats.departmentCounts).sort((a, b) => a.localeCompare(b)).map(dept => (
                    <SelectItem key={dept} value={dept} className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.author}
                onValueChange={(value) => handleFilterChange('author', value)}
              >
                <SelectTrigger className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600">
                  <SelectValue placeholder="All Authors" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600">
                  <SelectItem value="all" className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">All Authors</SelectItem>
                  {Object.keys(stats.authorCounts).sort((a, b) => a.localeCompare(b)).map(author => (
                    <SelectItem key={author} value={author} className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">{author}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.tag}
                onValueChange={(value) => handleFilterChange('tag', value)}
              >
                <SelectTrigger className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600">
                  <SelectValue placeholder="All Tags" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600">
                  <SelectItem value="all" className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">All Tags</SelectItem>
                  {Object.keys(stats.tagCounts).sort((a, b) => a.localeCompare(b)).map(tag => (
                    <SelectItem key={tag} value={tag} className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">{tag}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={sortOrder}
                onValueChange={handleSortChange}
              >
                <SelectTrigger className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600">
                  <SelectValue placeholder="Sort By Date" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600">
                  <SelectItem value="newest" className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">Newest First</SelectItem>
                  <SelectItem value="oldest" className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">Oldest First</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                variant="default"
                onClick={clearFilters}
                className="whitespace-nowrap bg-green-600 hover:bg-green-700 text-white"
              >Clear Filters</Button>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/10 p-4 rounded">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-8 text-gray-900 dark:text-white">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          </div>
        )}

        {/* SOP List */}
        {!loading && !error && (
          <div className="grid grid-cols-1 gap-4">
            {displayedSops.map(sop => (
              <div key={sop.id} className="relative flex flex-col py-3 hover:bg-gray-100 dark:hover:bg-gray-800/50 border-b dark:border-gray-700 last:border-b-0">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    {/* Title - Hyperlinked but not displaying the URL */}
                    <div className="flex items-center gap-4">
                      <a
                        href={sop.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-base font-semibold text-gray-900 dark:text-gray-100 hover:text-green-600 dark:hover:text-green-400"
                      >
                        {sop.title}
                      </a>
                    </div>
                    
                    {/* Short Summary */}
                    {sop.shortSummary && (
                      <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                        {sop.shortSummary}
                      </div>
                    )}

                    {/* Detailed Summary in Collapsible */}
                    {sop.detailedSummary && (
                      <Collapsible className="mt-2">
                        <CollapsibleTrigger className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                          <ChevronDown className="h-4 w-4" />
                          <span className="text-xs">More details</span>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2">
                          <div className="text-sm text-gray-600 dark:text-gray-300 pl-5 border-l-2 border-gray-200 dark:border-gray-700">
                            {sop.detailedSummary.split('\n\n').map((paragraph, index) => (
                              <p key={index} className="mb-2">{paragraph}</p>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                    {/* Authors and Date */}
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {sop.author1 && `By: ${sop.author1}`}
                        {sop.author1 && sop.author2 && ', '}
                        {sop.author2}
                        {(sop.author1 || sop.author2) && ' • '}
                        {sop.dateCreated}
                      </span>
                      {appSettings.showSOPFlaggingFeature && (
                        sop.flaggedForReview ? (
                          <RemoveFlagDialog sop={sop} onFlagRemoved={refreshSOPs} />
                        ) : (
                          <FlagForReviewDialog sop={sop} onFlagged={refreshSOPs} />
                        )
                      )}
                    </div>
                    {/* Department and Tags - Separate row on mobile */}
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {sop.department.split(',').map((dept, index) => {
                        const trimmedDept = dept.trim();
                        return trimmedDept ? (
                          <span
                            key={`${trimmedDept}-${index}`}
                            className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${getDepartmentColorClass(trimmedDept, index)} cursor-pointer hover:opacity-80`}
                            onClick={() => {
                              handleFilterChange('department', trimmedDept);
                              // Scroll back to top when filter changes
                              window.scrollTo(0, 0);
                            }}
                          >
                            {trimmedDept}
                          </span>
                        ) : null;
                      })}
                      {sop.tags?.map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300 ring-1 ring-inset ring-gray-500/10 dark:ring-gray-700 cursor-pointer hover:opacity-80"
                          onClick={() => {
                            handleFilterChange('tag', tag);
                            // Scroll back to top when filter changes
                            window.scrollTo(0, 0);
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                      
                      {/* Flagged for Review Tag */}
                      {appSettings.showSOPFlaggingFeature && sop.flaggedForReview && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="inline-flex items-center rounded-md bg-red-50 dark:bg-red-900/20 px-2 py-0.5 text-xs font-medium text-red-600 dark:text-red-400 ring-1 ring-inset ring-red-500/10 dark:ring-red-700"
                              >
                                <Flag className="h-3 w-3 mr-1" />
                                Flagged For Review
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Flagged by: {sop.flaggedBy || 'Unknown'}</p>
                              <p>Reason: {sop.flagReason || 'No reason provided'}</p>
                              {sop.flaggedAt && <p>Date: {new Date(sop.flaggedAt).toLocaleDateString()}</p>}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <AddToFavoritesDialog sop={sop} />
                  </div>
                </div>
              </div>
            ))}

            {/* Load More Trigger */}
            {displayedSops.length < filteredSops.length && (
              <div ref={loadMoreRef} className="h-10" />
            )}
          </div>
        )}

        {/* No Results */}
        {!loading && !error && displayedSops.length === 0 && (
          <div className="text-center py-8 text-gray-600 dark:text-gray-400">
            No SOPs found matching your criteria.
          </div>
        )}
      </div>
    </div>
  );
};

export default SOPList;
